import {
  AfterViewInit,
  Component,
  ElementRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { combineLatest, distinctUntilChanged, filter, take } from 'rxjs';

import { DialogCloseReason, DialogService } from '@sui/dialog';
import { AdvancedSearchService } from '../../services/advanced-search.service';
import { PopoverBoundaryRegistry } from '@services/popover-boundary-registry.service';
import { UserFacadeService } from '@services/user-facade.service';

import * as feedActions from '../../actions/feed.actions';
import { AppFeatures } from '../../app.features';
import { AppState } from 'src/app/reducers';
import { Feed } from '../../models/core.models';
import { NomObjectClass, NomObjectTypes, SortDirection } from '../../constants';
import { FeedPostViewMode } from '../../services/feed.service';
import {
  PostAlgorithm,
  PostAlgorithmService,
} from '../../services/post-algorithm.service';
import { PropertyViewConfig } from '../../services/feed-settings.service';
import {
  ConfirmDialogComponent,
  ConfirmDialogOutput,
} from '../popups/confirm-dialog/confirm-dialog.component';
import {
  selectCurrentObject,
  selectFeed,
  selectFeedPostsViewMode,
  selectFeedSettings,
  selectFeedTotalItemsCount,
  selectFeedViewConfig,
  selectSelectedPostsCount,
  selectUser,
} from '../../reducers';

/** A menu row rendered inside one of the toolbar's dropdown panels. */
interface FeedMenuItem {
  text?: string;
  icon?: IconProp;
  value?: string;
  key?: string;
  isSeparator?: boolean;
  isTitle?: boolean;
  isToggle?: boolean;
  isRadio?: boolean;
  isSelected?: boolean;
  radioGroup?: string;
  action?: () => void;
  /** Trailing affordance (e.g. "schedule" on a feed-wide algorithm). */
  secondaryAction?: { icon: IconProp; tooltip?: string; action: () => void };
}

/**
 * A consecutive run of view rows. `radioGroup` set ⇒ the run is one mutually
 * exclusive group and renders inside a single `sui-radio-group` (a radio group
 * needs one wrapping element, and `role=radiogroup` must not enclose toggles).
 */
interface FeedMenuGroup {
  radioGroup?: string;
  /** Currently selected `value` within a radio run. */
  value?: string;
  items: FeedMenuItem[];
}

/**
 * Feed-results toolbar, projected into `post-list` via `[postListToolbar]`.
 * Feed-only: subscribes to feed store state to build its dropdowns and dispatches
 * directly (replaces the retired `FeedActionsService` config + bus).
 */
@Component({
  selector: 'app-feed-results-toolbar',
  templateUrl: './feed-results-toolbar.component.html',
  styleUrl: './feed-results-toolbar.component.scss',
  standalone: false,
})
export class FeedResultsToolbarComponent implements AfterViewInit {
  private store = inject(Store<AppState>);
  private translate = inject(TranslateService);
  private algorithmService = inject(PostAlgorithmService);
  private userFacade = inject(UserFacadeService);
  private dialogService = inject(DialogService);
  private advancedSearchService = inject(AdvancedSearchService);
  private boundaryRegistry = inject(PopoverBoundaryRegistry);
  private host = inject(ElementRef<HTMLElement>);

  // Permission gates (mirror the old FeedActionsService config gating).
  readonly canSelectContent = this.userFacade.hasPermissionSync(
    AppFeatures.FEED_SEARCH_RESULTS_SELECT_CONTENT,
  );
  readonly canMassOps = this.userFacade.hasPermissionSync(
    AppFeatures.ADVANCED_MASS_OPERATIONS_ROOT,
  );
  readonly canView = this.userFacade.hasPermissionSync(
    AppFeatures.FEED_SEARCH_RESULTS_SETTINGS_DISPLAY_SETTINGS,
  );
  readonly canSort = this.userFacade.hasPermissionSync(
    AppFeatures.FEED_SEARCH_RESULTS_SETTINGS_SORT_SETTINGS,
  );
  private readonly canRemoveFromFeed = this.userFacade.hasPermissionSync(
    AppFeatures.ADVANCED_MASS_OPERATIONS_REMOVE_FROM_FEED,
  );
  private readonly canShowSelectedOnly = this.userFacade.hasPermissionSync(
    AppFeatures.ADVANCED_MASS_OPERATIONS_SELECTED_ITEMS_IN_CURRENT_FEED_TAB,
  );

  private feed: Feed;
  private viewPostsMode: string;

  selectAllChecked = false;
  showSelectedOnly = false;
  feedSearchQuery = '';

  hasSelectedPosts = false;
  selectedCount = 0;
  selectedItems: FeedMenuItem[] = [];
  actionItems: FeedMenuItem[] = [];
  viewItems: FeedMenuItem[] = [];
  /** `viewItems` split into runs so each radio run can sit in one sui-radio-group. */
  viewGroups: FeedMenuGroup[] = [];
  sortFieldItems: FeedMenuItem[] = [];
  readonly sortOrderItems: FeedMenuItem[] = [
    {
      text: this.translate.instant('feed-actions.button.descending'),
      icon: ['fas', 'sort-amount-down'],
      value: 'desc',
      isRadio: true,
      radioGroup: 'sort-order',
    },
    {
      text: this.translate.instant('feed-actions.button.ascending'),
      icon: ['fas', 'sort-amount-up'],
      value: 'asc',
      isRadio: true,
      radioGroup: 'sort-order',
    },
  ];
  sortField: string;
  sortDirection: string;

  /** Full sort catalogue; filtered per feed `object_type_id` into `sortFieldItems`. */
  private readonly allSortItems: FeedMenuItem[] = this.buildSortCatalogue();

  /** Nearest `[popoverBoundary]` pane (feed page → `'body'`). */
  popoverContainer = signal<HTMLElement | string>('body');

  get selectAllTooltip(): string {
    return this.translate.instant('feed-actions.button.select-all');
  }
  get selectedLabel(): string {
    return this.translate.instant('feed-actions.button.selected');
  }
  get actionsLabel(): string {
    return this.translate.instant('feed-actions.button.actions');
  }
  get viewLabel(): string {
    return this.translate.instant('feed-actions.button.view');
  }
  get sortLabel(): string {
    return this.translate.instant('feed-actions.button.sort');
  }
  get sortDirIcon(): IconProp {
    return this.sortDirection === 'asc'
      ? ['fas', 'arrow-up-a-z']
      : ['fas', 'arrow-down-z-a'];
  }
  get sortFieldIcon(): IconProp {
    return this.sortFieldItems.find((i) => i.value === this.sortField)?.icon;
  }

  constructor() {
    this.store
      .select(selectFeed)
      .pipe(takeUntilDestroyed())
      .subscribe((feed) => {
        if (!feed) {
          return;
        }
        this.feed = feed;
        this.sortFieldItems = this.allSortItems.filter(
          (item) => this.sortApplies(item, feed.object_type_id),
        );
        this.selectedCount = feed.count_selected_posts || 0;
        this.hasSelectedPosts = this.selectedCount > 0;
        // Clearing the selection unchecks select-all.
        if (!this.hasSelectedPosts) {
          this.selectAllChecked = false;
        }
      });

    combineLatest([
      this.store.select(selectUser),
      this.store.select(selectCurrentObject).pipe(
        filter((obj) => !!obj && obj.object_class_id === NomObjectClass.FEED),
        distinctUntilChanged((a, b) => a.id === b.id),
      ),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([user, currentObject]) => {
        const algorithms = this.algorithmService
          .getAlgorithmsPerUser(user, currentObject)
          .sort((a, b) => a.group?.localeCompare(b.group));
        this.actionItems = this.buildActionItems(algorithms);
        this.selectedItems = this.buildSelectedItems(algorithms);
      });

    this.store
      .select(selectFeedSettings)
      .pipe(takeUntilDestroyed())
      .subscribe((feedSettings) => {
        this.sortDirection = feedSettings.sort_direction;
        this.sortField = feedSettings.order_field;
      });

    // view_posts_mode radios track the store's view mode, not config.selected.
    combineLatest([
      this.store.select(selectFeedViewConfig),
      this.store.select(selectFeedPostsViewMode),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(([config, viewPostsMode]) => {
        this.viewPostsMode = viewPostsMode;
        this.viewItems = this.buildViewItems(config || []);
        this.viewGroups = this.groupViewItems(this.viewItems);
      });

    this.advancedSearchService.searchQuery$
      .pipe(takeUntilDestroyed())
      .subscribe((query) => (this.feedSearchQuery = query));
  }

  ngAfterViewInit(): void {
    this.popoverContainer.set(
      this.boundaryRegistry.findContainer(this.host.nativeElement) ?? 'body',
    );
  }

  onSelectAllChange(selected: boolean): void {
    this.selectAllChecked = selected;
    this.store.dispatch(
      feedActions.selectAllPosts({ feedId: this.feed?.id, selected }),
    );
    setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
  }

  onRefresh(): void {
    this.store.dispatch(feedActions.loadFeedPosts({ feedId: this.feed?.id }));
  }

  onSortFieldChange(value: string): void {
    this.sortField = value;
    this.store.dispatch(feedActions.sortChanged({ sortField: value }));
  }

  onSortOrderChange(value: string): void {
    this.sortDirection = value;
    this.store.dispatch(
      feedActions.sortDirectionChanged({
        sortDirection: value as SortDirection,
      }),
    );
  }

  private buildSelectedItems(algorithms: PostAlgorithm[]): FeedMenuItem[] {
    const items: FeedMenuItem[] = [];

    if (this.canRemoveFromFeed) {
      items.push(
        {
          text: this.translate.instant('feed-actions.selected.remove-from-feed'),
          icon: ['far', 'times-circle'],
          action: () => this.removeSelected(),
        },
        { isSeparator: true },
      );
    }
    if (this.canShowSelectedOnly) {
      items.push(
        {
          text: this.translate.instant(
            'feed-actions.selected.show-selected-only',
          ),
          icon: ['fas', 'toggle-on'],
          isToggle: true,
          action: () => this.toggleShowSelectedOnly(),
        },
        { isSeparator: true },
      );
    }

    items.push(
      {
        isTitle: true,
        text: this.translate.instant(
          'feed-actions.heading.apply-action-to-selected-items',
        ),
      },
      { isSeparator: true },
      ...this.algorithmItems(algorithms, (algorithm) => ({
        action: () => this.runAlgorithmSelected(algorithm.id),
      })),
      { isSeparator: true },
      {
        text: this.translate.instant('feed-actions.button.clear-selection'),
        icon: ['fas', 'times'],
        action: () => this.clearSelection(),
      },
    );

    return items;
  }

  private buildActionItems(algorithms: PostAlgorithm[]): FeedMenuItem[] {
    return [
      {
        isTitle: true,
        text: this.translate.instant(
          'feed-actions.heading.apply-action-to-all-items',
        ),
      },
      { isSeparator: true },
      ...this.algorithmItems(algorithms, (algorithm) => ({
        action: () => this.runAlgorithmFeed(algorithm.id, false),
        secondaryAction: algorithm.canSchedule
          ? {
              icon: ['far', 'clock'],
              tooltip: this.translate.instant(
                'feed-actions.button.schedule-tooltip',
              ),
              action: () => this.runAlgorithmFeed(algorithm.id, true),
            }
          : undefined,
      })),
    ];
  }

  /** Flatten grouped algorithms into rows, inserting a separator on group change. */
  private algorithmItems(
    algorithms: PostAlgorithm[],
    handlersFor: (algorithm: PostAlgorithm) => Pick<
      FeedMenuItem,
      'action' | 'secondaryAction'
    >,
  ): FeedMenuItem[] {
    const rows: FeedMenuItem[] = [];
    let currentGroup = algorithms[0]?.group;
    algorithms.forEach((algorithm) => {
      if (currentGroup !== algorithm.group) {
        currentGroup = algorithm.group;
        rows.push({ isSeparator: true });
      }
      rows.push({
        text: algorithm.name,
        icon: algorithm.icon,
        value: String(algorithm.id),
        ...handlersFor(algorithm),
      });
    });
    return rows;
  }

  private buildViewItems(config: PropertyViewConfig[]): FeedMenuItem[] {
    const createItems = (configItem: PropertyViewConfig): FeedMenuItem[] => {
      if (configItem.isTitle) {
        return [
          { isSeparator: true },
          { text: configItem.label, isTitle: true },
        ];
      }
      return [
        {
          text: configItem.label,
          icon: configItem.icon,
          // Radio rows need a value to bind a sui-radio-group against.
          value: String(configItem.path ?? configItem.label),
          isToggle: !configItem.isRadio,
          isRadio: configItem.isRadio,
          isSelected:
            configItem.radioGroup === 'view_posts_mode'
              ? configItem.path === this.viewPostsMode
              : configItem.selected,
          radioGroup: configItem.radioGroup,
          action: () => this.onViewConfigChange(configItem),
        },
      ];
    };

    const tools = config.filter((c) => c.visibility?.includes('tools'));
    const properties = config.filter((c) => !c.visibility?.includes('tools'));

    const items: FeedMenuItem[] = [
      ...tools.flatMap(createItems),
      {
        isTitle: true,
        text: this.translate.instant('feed-actions.heading.metadata'),
      },
      ...properties.flatMap(createItems),
    ];

    // Drop a leading separator (a tools group emits one before its title).
    return items.filter((item, index) => !(index === 0 && item.isSeparator));
  }

  /** Collapses consecutive rows sharing a `radioGroup` into one group. */
  private groupViewItems(items: FeedMenuItem[]): FeedMenuGroup[] {
    const groups: FeedMenuGroup[] = [];

    for (const item of items) {
      const previous = groups[groups.length - 1];
      const runs =
        !!item.radioGroup && previous?.radioGroup === item.radioGroup;

      if (runs) {
        previous.items.push(item);
        if (item.isSelected) {
          previous.value = item.value;
        }
        continue;
      }

      groups.push({
        radioGroup: item.radioGroup,
        value: item.isSelected ? item.value : undefined,
        items: [item],
      });
    }

    return groups;
  }

  /** A radio run owns the whole group's selection; delegate to the picked row. */
  onViewRadioChange(group: FeedMenuGroup, value: unknown): void {
    group.items.find((item) => item.value === String(value))?.action?.();
  }

  private onViewConfigChange(configItem: PropertyViewConfig): void {
    if (configItem.isRadio && configItem.radioGroup === 'view_posts_mode') {
      this.store.dispatch(
        feedActions.setFeedPostViewMode({
          viewMode: String(configItem.path) as FeedPostViewMode,
        }),
      );
    } else {
      this.store.dispatch(
        feedActions.toggleViewConfigOption({ optionName: configItem.label }),
      );
    }
  }

  private toggleShowSelectedOnly(): void {
    this.showSelectedOnly = !this.showSelectedOnly;
    this.store.dispatch(
      feedActions.showSelectedPostsOnly({
        feedId: this.feed?.id,
        showSelectedOnly: this.showSelectedOnly,
      }),
    );
  }

  private clearSelection(): void {
    this.store.dispatch(
      feedActions.clearFeedSelectedItems({ feedId: this.feed?.id }),
    );
  }

  private runAlgorithmSelected(algorithmId: number): void {
    this.store
      .select(selectSelectedPostsCount)
      .pipe(take(1))
      .subscribe((itemCount) =>
        this.algorithmService.runPostAlgorithmSelected(
          this.feed?.id,
          algorithmId,
          itemCount,
          {},
          false,
        ),
      );
  }

  private runAlgorithmFeed(algorithmId: number, schedule: boolean): void {
    this.store
      .select(selectFeedTotalItemsCount)
      .pipe(take(1))
      .subscribe((itemCount) =>
        this.algorithmService.runPostAlgorithmFeed(
          this.feed?.id,
          algorithmId,
          itemCount,
          {},
          schedule,
        ),
      );
  }

  private removeSelected(): void {
    const applyToChild = this.userFacade.hasPermissionSync(
      AppFeatures.ADVANCED_MASS_OPERATIONS_REMOVE_FROM_FEED_APPLY_TO_CHILD,
    );
    const options = applyToChild
      ? [
          {
            key: 'applyToChildObjects',
            label: this.translate.instant('option.apply-to-child-objects'),
            value: false,
          },
        ]
      : [];

    const dialogRef = this.dialogService.openSimpleDialog<
      ConfirmDialogComponent,
      ConfirmDialogOutput
    >(ConfirmDialogComponent, {
      data: {
        message: this.translate.instant('remove-selected.posts.modal-message'),
        options,
      },
      closeOnClick: false,
    });

    dialogRef.closed.subscribe((event) => {
      if (event.reason !== DialogCloseReason.Confirm) {
        return;
      }
      const applytoChildObjects = event.data?.options?.find(
        (opt) => opt.key === 'applyToChildObjects',
      )?.value;
      this.store.dispatch(
        feedActions.removeSelectedPostsFromFeed({
          feedId: this.feed?.id,
          applytoChildObjects,
        }),
      );
    });
  }

  private sortApplies(item: FeedMenuItem, objectTypeId: number): boolean {
    const applicable = SORT_APPLICABILITY[item.value];
    return !applicable || applicable.includes(objectTypeId);
  }

  private buildSortCatalogue(): FeedMenuItem[] {
    const t = (key: string) => this.translate.instant(key);
    const fields: FeedMenuItem[] = [
      { value: 'post_time_published', icon: ['fas', 'upload'], text: t('feed-actions.button.published-time') },
      { value: 'post_time_created', icon: ['fas', 'circle-plus'], text: t('feed-actions.button.created-time') },
      { value: 'post_reach', icon: ['fab', 'chromecast'], text: t('feed-actions.button.potential-reach') },
      { value: 'post_reach_v2', icon: ['fab', 'chromecast'], text: t('feed-actions.button.estimated-reach') },
      { value: 'post_source_visits_per_day', icon: ['fab', 'chromecast'], text: t('feed-actions.button.source-daily-visits') },
      { value: 'post_rank', icon: ['fas', 'chart-line'], text: t('feed-actions.button.content-rank') },
      { value: 'post_title', icon: ['fas', 'font'], text: t('feed-actions.button.title') },
      { value: 'source_rank_global', icon: ['fas', 'trophy'], text: t('feed-actions.button.global-rank') },
      { value: 'source_rank_country', icon: ['fas', 'sort-numeric-up'], text: t('feed-actions.button.country-rank') },
      { value: 'post_social_followers', icon: ['fas', 'users'], text: t('feed-actions.button.followers') },
      { value: 'post_social_reactions_count', icon: ['fas', 'heart-pulse'], text: t('feed-actions.button.social-reactions') },
      { value: 'post_social_interactions_count', icon: ['fas', 'hand-pointer'], text: t('feed-actions.button.social-interactions') },
      { value: 'post_social_engagements_count', icon: ['fas', 'hand-sparkles'], text: t('feed-actions.button.social-engagements') },
      { value: 'post_social_shares_count', icon: ['fas', 'retweet'], text: t('feed-actions.button.social-shares') },
      { value: 'post_social_comments_count', icon: ['fas', 'comments'], text: t('feed-actions.button.social-comments') },
      { value: 'post_social_likes_count', icon: ['fas', 'heart'], text: t('feed-actions.button.social-likes') },
      { value: 'post_social_dislikes_count', icon: ['fas', 'thumbs-down'], text: t('feed-actions.button.social-dislikes') },
    ];
    return fields.map((item) => ({ ...item, isRadio: true, radioGroup: 'sort' }));
  }
}

/** Sort fields limited to specific feed object types (others apply everywhere). */
const SORT_APPLICABILITY: Record<string, number[]> = {
  post_title: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.ONLINE_FEED,
    NomObjectTypes.STORY_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  source_rank_global: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.ONLINE_FEED,
    NomObjectTypes.STORY_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  source_rank_country: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.ONLINE_FEED,
    NomObjectTypes.STORY_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_followers: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_reactions_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_interactions_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_engagements_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_shares_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_comments_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_likes_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
  post_social_dislikes_count: [
    NomObjectTypes.COMBINED_FEED,
    NomObjectTypes.SOCIAL_FEED,
    NomObjectTypes.CLIPPING_FEED,
  ],
};
